/**
 * PLP utilities.
 *
 * Note: This file intentionally contains shared pure helpers used across PLP presentation/hooks.
 */

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeSidForComparison(value: string | undefined | null): string {
  const raw = value ?? '';
  let current = raw.toString().trim();
  if (!current) return '';

  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      current = next;
    } catch {
      break;
    }
  }
  return current.replace(/=+$/g, '');
}

function safeAtob(value: string): string {
  if (!value) return '';
  try {
    if (typeof globalThis.atob === 'function') return globalThis.atob(value);
    return '';
  } catch {
    return '';
  }
}

function safeDecodeURIComponent(value: string): string {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function decodeSidList(value: string | undefined | null): string[] {
  const raw = value?.trim() ?? '';
  if (!raw) return [];

  const decodedUri = safeDecodeURIComponent(raw);
  const base64Decoded = safeAtob(decodedUri);
  const source = base64Decoded || decodedUri;

  return source
    .split(',')
    .map((sid) => normalizeSidForComparison(sid))
    .filter(Boolean);
}

export function getSelectedStoreBySid<TStore extends { sid?: string | undefined | null }>(
  cityStoreMapUnknown: unknown,
  sids: string[]
): TStore | null {
  if (!isObject(cityStoreMapUnknown)) return null;
  const cityStoreMap = cityStoreMapUnknown as Record<string, unknown>;

  for (const cityName of Object.keys(cityStoreMap)) {
    const cityStoresUnknown = cityStoreMap[cityName];
    if (!Array.isArray(cityStoresUnknown)) continue;
    const cityStores = cityStoresUnknown as TStore[];
    const matched = cityStores.find((store) =>
      sids.includes(normalizeSidForComparison(store?.sid))
    );
    if (matched) return matched;
  }

  return null;
}

export function getSelectedStoresBySids<TStore extends { sid?: string | undefined | null }>(
  cityStoreMapUnknown: unknown,
  sids: string[]
): TStore[] {
  if (!isObject(cityStoreMapUnknown)) return [];
  if (!Array.isArray(sids) || sids.length === 0) return [];
  const cityStoreMap = cityStoreMapUnknown as Record<string, unknown>;
  const sidSet = new Set(sids);
  const selected: TStore[] = [];

  for (const cityName of Object.keys(cityStoreMap)) {
    const cityStoresUnknown = cityStoreMap[cityName];
    if (!Array.isArray(cityStoresUnknown)) continue;
    const cityStores = cityStoresUnknown as TStore[];
    for (const store of cityStores) {
      const normalized = normalizeSidForComparison(store?.sid);
      if (normalized && sidSet.has(normalized)) selected.push(store);
    }
  }

  return selected;
}

/**
 * Derive the composite `sid` for a city catalog page (`/stores/<city>/catalog.html`) from the
 * `cityToStoresMap` returned by `/physical-store/stores-details`.
 *
 * Shared by SSR (`server/entry-server.tsx`) and the client (`StorePlpPage`) so the composite
 * sid — and therefore the PLP listing key — is identical between the server render and
 * hydration, instead of the client deriving it after an extra client-only fetch.
 */
export function getCitySidFromCityToStoresMap(
  cityToStoresMapUnknown: unknown,
  citySlug: string | undefined | null
): string | undefined {
  if (!citySlug || !isObject(cityToStoresMapUnknown)) return undefined;

  const cityDisplay = citySlug
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');

  const map = cityToStoresMapUnknown as Record<string, unknown>;
  const key = Object.keys(map).find((k) => k.trim().toLowerCase() === cityDisplay.toLowerCase());
  if (!key) return undefined;

  const stores = map[key];
  if (!Array.isArray(stores) || stores.length === 0) return undefined;

  const sids = (stores as { sid?: string }[]).map((s) => s?.sid).filter(Boolean) as string[];
  return sids.length > 0 ? globalThis.btoa(sids.join(',')) : undefined;
}

export function getGoogleMapDirectionsLink(destinationPlaceId: string): string {
  return `https://www.google.com/maps/search/?api=1&query=BlueStone+Jewellery&query_place_id=${encodeURIComponent(
    destinationPlaceId
  )}`;
}

export function normalizeBluestoneCanonical(raw: string): string {
  if (!raw) return 'https://www.bluestone.com/';
  try {
    const resolved = raw.trim().replace(/__BASE_URL__/g, 'https://www.bluestone.com');
    const url = new URL(resolved, 'https://www.bluestone.com');
    const path = url.pathname || '/';
    return `https://www.bluestone.com${path}`;
  } catch {
    return 'https://www.bluestone.com/';
  }
}

